import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './Details.css';

import Loader from '../components/layout/preloader';
import Header from '../components/layout/header';
import Footer from '../components/layout/footer';
import { Link } from 'react-router-dom';

import ChairModel from '../asserts/children_chair.glb';
import Btn2 from '../components/common/btn2';
import CurvedLoop from '../components/layout/Curved Loop';
import { supabase } from '../supabaseClient';

gsap.registerPlugin(ScrollTrigger);

const Details = () => {
  const PRELOADER = 4.1;

  const [hero, setHero] = useState(null);
  const [about, setAbout] = useState(null);
  const [points, setPoints] = useState([]);
  const [expectations, setExpectations] = useState(null);
  const [events, setEvents] = useState([]);
  const [vrOuter, setVrOuter] = useState(null);
  const [vrInner, setVrInner] = useState(null);
  const [dataReady, setDataReady] = useState(false);

  const backBtnRef = useRef();
  const frameRef = useRef();
  const imgRef = useRef();
  const labelRef = useRef();
  const heroTitleRef = useRef();
  const subtitleRef = useRef();

  const aboutRef = useRef();
  const aboutTitleRef = useRef();
  const aboutText1Ref = useRef();
  const aboutText2Ref = useRef();
  const listItemsRef = useRef();
  const modelRef = useRef();

  const boothRef = useRef();
  const boothTitleRef = useRef();
  const boothCardRef = useRef();
  const boothInfoRef = useRef();
  const eventsRef = useRef();

  useEffect(() => {
    const fetchAll = async () => {
      const [heroRes, aboutRes, pointsRes, expectRes, eventsRes, vrOuterRes, vrInnerRes] = await Promise.all([
        supabase.from('details_hero').select('*').single(),
        supabase.from('details_about').select('*').single(),
        supabase.from('details_about_points').select('*').order('display_order'),
        supabase.from('details_expectations').select('*').single(),
        supabase.from('details_special_events').select('*').order('display_order'),
        supabase.from('details_vr_outer').select('*').single(),
        supabase.from('details_vr_inner').select('*').single(),
      ]);
      setHero(heroRes.data || null);
      setAbout(aboutRes.data || null);
      setPoints(pointsRes.data || []);
      setExpectations(expectRes.data || null);
      setEvents(eventsRes.data || []);
      setVrOuter(vrOuterRes.data || null);
      setVrInner(vrInnerRes.data || null);
      setDataReady(true);
    };
    fetchAll();
  }, []);

  useEffect(() => {
    if (!dataReady) return;

    const ctx = gsap.context(() => {

      gsap.fromTo(backBtnRef.current,
        { x: -60, opacity: 0 },
        { x: 0, opacity: 1, duration: 0.7, ease: 'power3.out', delay: PRELOADER }
      );

      gsap.fromTo(frameRef.current,
        { clipPath: 'inset(0% 0% 100% 0%)', opacity: 0 },
        { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1, duration: 1.3, ease: 'expo.out', delay: PRELOADER + 0.2 }
      );

      gsap.fromTo(imgRef.current,
        { scale: 1.15 },
        { scale: 1, duration: 1.6, ease: 'power3.out', delay: PRELOADER + 0.2 }
      );

      gsap.fromTo(labelRef.current,
        { opacity: 0 },
        {
          duration: 0.7, delay: PRELOADER + 0.9,
          keyframes: [
            { opacity: 0, duration: 0.06 }, { opacity: 1, duration: 0.06 },
            { opacity: 0, duration: 0.06 }, { opacity: 0.5, duration: 0.06 },
            { opacity: 0, duration: 0.06 }, { opacity: 1, duration: 0.4 },
          ]
        }
      );

      gsap.fromTo(heroTitleRef.current,
        { y: -60, opacity: 0, skewY: -4, letterSpacing: '16px' },
        { y: 0, opacity: 1, skewY: 0, letterSpacing: '3.333px', duration: 1, ease: 'expo.out', delay: PRELOADER + 1.0 }
      );

      gsap.fromTo(subtitleRef.current,
        { opacity: 0, filter: 'blur(6px)', y: 15 },
        { opacity: 1, filter: 'blur(0px)', y: 0, duration: 0.8, ease: 'power3.out', delay: PRELOADER + 1.2 }
      );

      gsap.fromTo(aboutTitleRef.current,
        { opacity: 0, x: -70, skewX: -6 },
        { opacity: 1, x: 0, skewX: 0, duration: 1, ease: 'expo.out', scrollTrigger: { trigger: aboutRef.current, start: 'top 75%' } }
      );

      gsap.fromTo([aboutText1Ref.current, aboutText2Ref.current],
        { opacity: 0, y: 35, filter: 'blur(6px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.85, stagger: 0.2, ease: 'power3.out', scrollTrigger: { trigger: aboutRef.current, start: 'top 72%' } }
      );

      gsap.fromTo('.details-about__list-item',
        { opacity: 0, x: 50 },
        { opacity: 1, x: 0, duration: 0.5, stagger: 0.12, ease: 'back.out(1.5)', scrollTrigger: { trigger: aboutRef.current, start: 'top 60%' } }
      );

      gsap.fromTo(modelRef.current,
        { opacity: 0, y: 80, scale: 0.85 },
        { opacity: 1, y: 0, scale: 1, duration: 1.2, ease: 'elastic.out(1, 0.5)', scrollTrigger: { trigger: aboutRef.current, start: 'top 65%' } }
      );

      gsap.fromTo(boothTitleRef.current,
        { opacity: 0, letterSpacing: '14px', y: 30 },
        { opacity: 1, letterSpacing: '2px', y: 0, duration: 1, ease: 'expo.out', scrollTrigger: { trigger: boothRef.current, start: 'top 78%' } }
      );

      gsap.fromTo(boothCardRef.current,
        { clipPath: 'inset(100% 0% 0% 0%)', opacity: 0 },
        { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1, duration: 1.3, ease: 'expo.out', scrollTrigger: { trigger: boothCardRef.current, start: 'top 80%' } }
      );

      gsap.fromTo(boothInfoRef.current,
        { opacity: 0, y: 60 },
        { opacity: 1, y: 0, duration: 1, ease: 'power3.out', scrollTrigger: { trigger: boothInfoRef.current, start: 'top 80%' } }
      );

      gsap.fromTo('.details-booth__event',
        { opacity: 0, x: 50, scale: 0.9 },
        { opacity: 1, x: 0, scale: 1, duration: 0.6, stagger: 0.15, ease: 'back.out(1.5)', scrollTrigger: { trigger: eventsRef.current, start: 'top 80%' } }
      );

      const card = boothCardRef.current;
      if (card) {
        card.addEventListener('mousemove', (e) => {
          const rect = card.getBoundingClientRect();
          const rx = ((e.clientY - rect.top) / rect.height - 0.5) * 10;
          const ry = ((e.clientX - rect.left) / rect.width - 0.5) * -10;
          gsap.to(card, { rotateX: rx, rotateY: ry, duration: 0.4, ease: 'power2.out', transformPerspective: 1200 });
        });
        card.addEventListener('mouseleave', () => {
          gsap.to(card, { rotateX: 0, rotateY: 0, duration: 0.6, ease: 'elastic.out(1, 0.4)' });
        });
      }

    });

    return () => ctx.revert();
  }, [dataReady]);

  return (
    <>
      <Loader />
      <Header />

      <main>
        <section className="details-hero">
          <Link to="/exhibtor" className="no-underline-link" ref={backBtnRef}>
            <button className="btn2" type="button" aria-label="Go back to exhibitors list">
              <svg className="arrow2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
              </svg>
              {hero?.back_btn_en || ''}
            </button>
          </Link>

          <div className="details-frame" ref={frameRef}>
            <img src={hero?.hero_image || ''} alt={hero?.title_en || ''} className="details-frame__img" ref={imgRef} />
            <div className="details-frame__overlay">
              <div className="details-frame__content">
                <span className="labell" ref={labelRef}>{hero?.label_en || ''}</span>
                <h1 className="title" ref={heroTitleRef}>{hero?.title_en || ''}</h1>
                <p className="subtitle" ref={subtitleRef}>{hero?.subtitle_en || ''}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="details-about" ref={aboutRef}>
          <div className="details-about__left">
            <h2 className="details-about__title" ref={aboutTitleRef}>{about?.title_en || ''}</h2>
            <p className="details-about__text" ref={aboutText1Ref}>{about?.content_en || ''}</p>
            <div className="details-about__block">
              <h3 className="details-about__subtext">{about?.philosophy_title_en || ''}</h3>
              <p className="details-about__text" ref={aboutText2Ref}>{about?.philosophy_content_en || ''}</p>
            </div>
            <div className="details-about__block">
              <h3 className="details-about__subtext">ACHIEVEMENTS</h3>
              <ul className="details-about__list" ref={listItemsRef}>
                {points.map((point) => (
                  <li className="details-about__list-item" key={point.id}>{point.point_en}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="details-about__right" ref={modelRef}>
            <model-viewer
              src={ChairModel}
              camera-controls auto-rotate disable-tap disable-pan disable-zoom
              camera-orbit="0deg 90deg 100%"
              min-camera-orbit="0deg 90deg 100%"
              max-camera-orbit="360deg 90deg 100%"
              shadow-intensity="1" shadow-softness="1"
              ar ar-modes="webxr scene-viewer quick-look"
            >
              <div className="progress-bar" slot="progress-bar">
                <div className="update-bar"></div>
              </div>
            </model-viewer>
          </div>
        </section>

        <CurvedLoop marqueeText="Interior Design * Interior Design * Interior Design *" speed={2} curveAmount={400} direction="right" interactive className="custom-text-style" />

        <section className="details-booth" ref={boothRef}>
          <div className="details-booth__header">
            <h2 className="details-booth__title" ref={boothTitleRef}>{vrOuter?.title_en || ''}</h2>
            <p className="details-booth__subtitle">{vrOuter?.subtitle_en || ''}</p>
          </div>

          <div className="details-booth__card" ref={boothCardRef}>
            <img src={vrOuter?.preview_image || ''} alt={`${hero?.title_en || 'Exhibitor'} Booth Preview`} className="details-booth__img" />
            <div className="details-booth__overlay">
              <span className="details-booth__vr-label">{vrOuter?.interactive_label_en || ''}</span>
              <Link to="/vr-viewer" className="no-underline-link">
                <Btn2 btnt={vrOuter?.open_btn_en || ''} aria-label={`Open VR viewer for ${hero?.title_en || 'Exhibitor'} booth`} />
              </Link>
            </div>
          </div>

          <div className="details-booth__info" ref={boothInfoRef}>
            <div className="details-booth__info-left">
              <h3 className="details-booth__info-title">{expectations?.title_en || ''}</h3>
              <p className="details-booth__info-text">{expectations?.content_en || ''}</p>
              <div className="details-booth__meta">
                <div className="details-booth__meta-item">
                  <span className="details-booth__meta-label">{expectations?.booth_label_en || ''}</span>
                  <div className="details-booth__meta-row">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    <span className="details-booth__meta-value">{expectations?.booth_value || ''}</span>
                  </div>
                </div>
                <div className="details-booth__meta-item">
                  <span className="details-booth__meta-label">{expectations?.location_label_en || ''}</span>
                  <div className="details-booth__meta-row">
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" />
                    </svg>
                    <span className="details-booth__meta-value">{expectations?.location_value_en || ''}</span>
                  </div>
                </div>
              </div>
              <Link to="/book" className="no-underline-link">
                <Btn2 btnt={expectations?.cta_text_en || ''} />
              </Link>
            </div>

            <div className="details-booth__info-right" ref={eventsRef}>
              <h3 className="details-booth__info-title">SPECIAL EVENTS</h3>
              <div className="details-booth__events">
                {events.map((ev) => (
                  <div className="details-booth__event" key={ev.id}>
                    <div className="details-booth__event-date">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
                      </svg>
                      <span>{ev.event_date_en}</span>
                    </div>
                    <div className="details-booth__event-time">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
                      </svg>
                      <span>{ev.event_time}</span>
                    </div>
                    <p className="details-booth__event-name">{ev.event_title_en}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
};

export default Details;